import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";

const round = (value) => Math.round(value).toFixed(0);

const STATUS_STYLES = {
  headwind: "status-headwind",
  crosswind: "status-crosswind",
  tailwind: "status-tailwind",
};

const STATUS_ICONS = {
  headwind: "↑",
  crosswind: "↔",
  tailwind: "↓",
};

const AirportRunwayCard = (props) => {
  const { t } = useTranslation();
  const { active, ident, invertMark, heading, length, width, ils } = props;
  const status = active?.status;

  const statusLabel = status
    ? t(`runway.${status}`)
    : t("airport.variableWinds");

  const isRecommended = status === "headwind";

  return (
    <article
      className={clsx(
        "glass-card rounded-card p-3 sm:p-5 shadow-card card-enter flex flex-col h-full",
        "border transition-all duration-300",
        isRecommended
          ? "border-aviation-radarGreen/40 shadow-glow-green"
          : "border-white/10"
      )}
      aria-label={`${t("runway.heading")} ${ident}`}
    >
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
          {/* Runway designator */}
          <div
            className={clsx(
              "px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-lg sm:text-xl font-extrabold font-mono tracking-wider border shrink-0",
              isRecommended
                ? "bg-aviation-radarGreen/15 text-aviation-radarGreen border-aviation-radarGreen/40"
                : status === "crosswind"
                ? "bg-aviation-amber/10 text-aviation-amber border-aviation-amber/30"
                : "bg-white/5 text-slate-400 border-white/10"
            )}
          >
            {ident}
          </div>

          {/* Recommended badge */}
          {isRecommended && (
            <span
              className="px-1.5 sm:px-2 py-0.5 rounded text-[8px] sm:text-[10px] font-bold uppercase tracking-wider text-aviation-radarGreen bg-aviation-radarGreen/10 border border-aviation-radarGreen/30 animate-glow-pulse shrink-0 max-w-full text-center"
              aria-label={t("runway.recommended")}
            >
              {t("runway.recommended")}
            </span>
          )}
        </div>

        {/* Status badge */}
        <div className="text-left sm:text-right mt-1 sm:mt-0 w-full sm:w-auto">
          <span
            className={clsx(
              "inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] sm:text-xs font-bold shrink-0",
              STATUS_STYLES[status] ?? "text-slate-500"
            )}
            aria-label={statusLabel}
          >
            <span aria-hidden="true">{STATUS_ICONS[status] ?? "~"}</span>
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Wind data */}
      {active?.headtailwindType && (
        <div className="mb-4 p-2 sm:p-3 rounded-lg bg-white/[0.03] border border-white/[0.05] space-y-1.5 flex-grow-0">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <span className="text-[10px] sm:text-xs text-slate-400 break-words">
              {t(`runway.${active.headtailwindType}`)}
            </span>
            <span className="font-mono text-xs sm:text-sm font-bold text-white shrink-0">
              {round(active.headtailwind)}{" "}
              <span className="text-[10px] sm:text-xs font-normal text-slate-500">
                {t("wind.kts")}
              </span>
            </span>
          </div>
          <div className="flex flex-wrap justify-between items-center gap-2">
            <span className="text-[10px] sm:text-xs text-slate-400 break-words">{t("runway.crosswind")}</span>
            <span className="font-mono text-xs sm:text-sm font-bold text-white shrink-0">
              {round(active.crosswind)}{" "}
              <span className="text-[10px] sm:text-xs font-normal text-slate-500">
                {t("wind.kts")}
              </span>
            </span>
          </div>
          <p className="text-[9px] sm:text-[10px] text-slate-600 leading-tight">
            {t("runway.crosswindFrom")} {active.crosswindSide}
          </p>
        </div>
      )}

      {/* Runway specs */}
      <div className="space-y-1.5 mt-auto">
        <div className="flex flex-wrap justify-between items-center gap-1">
          <span className="text-[10px] sm:text-xs text-slate-500">{t("runway.heading")}</span>
          <span className="font-mono text-[10px] sm:text-xs text-slate-300">
            {round(heading)}° T
          </span>
        </div>
        <div className="flex flex-wrap justify-between items-center gap-1">
          <span className="text-[10px] sm:text-xs text-slate-500">{t("runway.length")}</span>
          <span className="font-mono text-[10px] sm:text-xs text-slate-300">
            {round(length)} ft
          </span>
        </div>
        <div className="flex flex-wrap justify-between items-center gap-1">
          <span className="text-[10px] sm:text-xs text-slate-500">{t("runway.width")}</span>
          <span className="font-mono text-[10px] sm:text-xs text-slate-300">
            {round(width)} ft
          </span>
        </div>
        <div className="flex flex-wrap justify-between items-center gap-1">
          <span className="text-[10px] sm:text-xs text-slate-500">{t("runway.invertedMark")}</span>
          <span className="font-mono text-[10px] sm:text-xs px-1.5 py-0.5 rounded border border-white/10 text-slate-400">
            {invertMark}
          </span>
        </div>
        <div className="flex flex-wrap justify-between items-center gap-1">
          <span className="text-[10px] sm:text-xs text-slate-500">{t("runway.ils")}</span>
          <span className="font-mono text-[10px] sm:text-xs text-slate-300">
            {ils ? (
              <span className="text-aviation-cyan break-all">
                {ils.freq}/{ils.course}°
              </span>
            ) : (
              <span className="text-slate-600">{t("runway.noIls")}</span>
            )}
          </span>
        </div>
      </div>
    </article>
  );
};

export default AirportRunwayCard;
